import React, { useEffect, useRef, useState } from 'react';
import MUIAutocomplete from '@material-ui/lab/Autocomplete';
import MUITextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '../../Typography';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import parseGoogleAddress from './parseGoogleAddress';

let loaderPromise = null;
const googleMapsApiKey = (process.env.REACT_APP_KEY_GOOGLE_MAPS || '').trim();

function loadGoogleMaps() {
  if (loaderPromise) return loaderPromise;
  if (window.google?.maps?.places) return Promise.resolve(window.google);

  if (!googleMapsApiKey) {
    return Promise.reject(
      new Error('Missing REACT_APP_KEY_GOOGLE_MAPS. Set it in .env.development and restart react-scripts.')
    );
  }

  setOptions({
    key: googleMapsApiKey,
    v: 'weekly',
  });

  loaderPromise = importLibrary('places')
    .then(() => window.google)
    .catch((error) => {
      loaderPromise = null;
      throw error;
    });

  return loaderPromise;
}

function createNewSessionToken() {
  if (!window.google?.maps?.places?.AutocompleteSessionToken) {
    return null;
  }

  return new window.google.maps.places.AutocompleteSessionToken();
}

function isAutocompleteSuggestionAvailable() {
  return Boolean(window.google?.maps?.places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions);
}

function shouldDisableAutocomplete(error) {
  const message = String(error?.message || error || '');

  return (
    message.includes('LegacyApiNotActivatedMapError') ||
    message.includes('legacy API') ||
    message.includes('not enabled for your project')
  );
}

/**
 * Address input with Google Places suggestions and parsed address details.
 */
export default function AddressAutocomplete({
  label,
  placeholder,
  value,
  onChange,
  onAddressSelect,
  error,
  helperText,
  required,
}) {
  const [ready, setReady] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [autocompleteAvailable, setAutocompleteAvailable] = useState(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    let mounted = true;

    loadGoogleMaps()
      .then(() => {
        if (!mounted) {
          return;
        }

        setReady(true);

        const canUseAutocomplete = isAutocompleteSuggestionAvailable();
        setAutocompleteAvailable(canUseAutocomplete);
        setSessionToken(canUseAutocomplete ? createNewSessionToken() : null);
      })
      .catch((err) => {
        console.error('Failed to load Google Maps:', err);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!ready || !autocompleteAvailable) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    const input = (value || '').trim();
    if (!input) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;

    const timeoutId = setTimeout(async () => {
      try {
        setLoading(true);

        const request = {
          input,
          includedRegionCodes: ['us'],
        };

        if (sessionToken) {
          request.sessionToken = sessionToken;
        }

        const { AutocompleteSuggestion } = window.google.maps.places;
        const { suggestions = [] } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        setOptions(
          suggestions
            .map((suggestion) => {
              const prediction = suggestion.placePrediction;
              const description = prediction?.text?.text || prediction?.mainText?.text || '';

              if (!description || !prediction) {
                return null;
              }

              return {
                description,
                placePrediction: prediction,
              };
            })
            .filter(Boolean)
        );
      } catch (error) {
        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        setOptions([]);

        if (shouldDisableAutocomplete(error)) {
          setAutocompleteAvailable(false);
          setSessionToken(null);
          return;
        }

        console.error('Failed to fetch place suggestions:', error);
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [autocompleteAvailable, ready, sessionToken, value]);

  const handleInputChange = (_event, newInputValue, reason) => {
    if (reason === 'input') {
      onChange(newInputValue);
    }

    if (reason === 'clear') {
      onChange('');
      setOptions([]);
      setSessionToken(autocompleteAvailable ? createNewSessionToken() : null);
    }
  };

  const handleSelect = async (_event, selected) => {
    if (!selected) return;

    if (typeof selected === 'string') {
      onChange(selected);
      setOptions([]);
      return;
    }

    onChange(selected.description || '');
    setOptions([]);

    if (!selected.placePrediction) {
      return;
    }

    try {
      const place = selected.placePrediction.toPlace();

      await place.fetchFields({
        fields: ['formattedAddress', 'addressComponents'],
      });

      if (place?.addressComponents) {
        const parsed = parseGoogleAddress(place.addressComponents);
        onAddressSelect?.(parsed);
      }

      setSessionToken(createNewSessionToken());
    } catch (err) {
      console.error('Failed to get place details:', err);
    }
  };

  return (
    <>
      {label && (
        <Typography weight="500" fontFamily="lexend" color="label" gutter="label" variant="body2">
          {/* removed for consistency but we may want to re-add */}
          {/* {label} {required && '*'}  */}
          {label} {required}
        </Typography>
      )}
      <MUIAutocomplete
        freeSolo
        options={options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.description || '')}
        filterOptions={(x) => x}
        inputValue={value || ''}
        onInputChange={handleInputChange}
        onChange={handleSelect}
        loading={loading}
        noOptionsText={
          autocompleteAvailable ? 'Type to search an address' : 'Autocomplete unavailable. Enter address manually'
        }
        renderInput={(params) => (
          <MUITextField
            {...params}
            variant="filled"
            placeholder={placeholder}
            hiddenLabel
            fullWidth
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off',
            }}
            error={!!error}
            helperText={helperText}
          />
        )}
        renderOption={(option) => <span>{typeof option === 'string' ? option : option.description}</span>}
      />
    </>
  );
}
